import React from "react";
import { Input, InputNumber, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
// Styles
import "components/InputWithLabel/InputWithLabel.scss";

export function formatConstraintValue(value, decimals) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  const d = decimals != null ? Number(decimals) : null;
  if (d === 0) return String(Math.round(n));
  if (d != null && d > 0) return n.toFixed(d);
  return String(n);
}

export function getFieldConstraintsLabel(min, max, decimals) {
  const hasMin = min != null && min !== "";
  const hasMax = max != null && max !== "";
  if (!hasMin && !hasMax) return null;
  const parts = [];
  if (hasMin) parts.push(`min: ${formatConstraintValue(min, decimals)}`);
  if (hasMax) parts.push(`max: ${formatConstraintValue(max, decimals)}`);
  return parts.join(", ");
}

const InputWithLabel = (props) => {
  const {
    label,
    value,
    isDisabled,
    measureUnitValue,
    isUnitBeforeText,
    width,
    min,
    max,
    decimals,
    itemKey,
    onChange,
    onCommit,
    error,
    isSaving,
    multiline = false,
    suggestedValueDisplay,
    onApplySuggestedValue,
  } = props;

  const handleChange = (next) => {
    if (onChange && itemKey != null) onChange(itemKey, next);
  };

  const handleCommit = () => {
    if (onCommit && itemKey != null) onCommit(itemKey);
  };

  const handlePressEnter = (e) => {
    const target = e?.target;
    if (target && typeof target.blur === "function") {
      target.blur();
      return;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const constraintsLabel = getFieldConstraintsLabel(min, max, decimals);
  const showSuggestedValue =
    !isDisabled &&
    suggestedValueDisplay != null &&
    suggestedValueDisplay !== "" &&
    typeof onApplySuggestedValue === "function";

  // decimals === null is the explicit signal for a text field (e.g. shorthand).
  // Anything else (number, or omitted by older callers) means numeric input.
  const isTextField = decimals === null;
  const renderNumeric = !isDisabled && !isTextField;

  const sharedClassName = [
    isDisabled ? "ant-input-number-disabled" : "",
    !width ? "input-field-units" : "",
    error ? "input-with-label__error" : "",
    multiline ? "input-with-label__multiline-input" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const savingSuffix = isSaving ? <LoadingOutlined spin /> : null;

  const control = renderNumeric ? (
    <InputNumber
      className={sharedClassName}
      style={{ width }}
      disabled={isDisabled}
      value={value}
      min={min ?? undefined}
      max={max ?? undefined}
      precision={decimals ?? undefined}
      onChange={handleChange}
      onPressEnter={handlePressEnter}
      onBlur={handleCommit}
      addonBefore={isUnitBeforeText ?? ""}
      addonAfter={savingSuffix ?? measureUnitValue ?? ""}
    />
  ) : multiline ? (
    <Input.TextArea
      className={sharedClassName}
      style={{ width }}
      disabled={isDisabled}
      readOnly={isDisabled}
      autoSize={{ minRows: 1, maxRows: 16 }}
      value={value ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      onPressEnter={handlePressEnter}
      onBlur={handleCommit}
    />
  ) : (
    <Input
      className={sharedClassName}
      style={{ width }}
      disabled={isDisabled}
      value={value ?? ""}
      onChange={(e) => handleChange(e.target.value)}
      onPressEnter={handlePressEnter}
      onBlur={handleCommit}
      addonBefore={isUnitBeforeText ?? ""}
      addonAfter={measureUnitValue ?? ""}
      suffix={savingSuffix}
    />
  );

  const wrapped = error ? (
    <Tooltip title={error} color="red" placement="top" open>
      {control}
    </Tooltip>
  ) : (
    control
  );

  return (
    <div
      className={`input-field-with-label${
        multiline ? " input-field-with-label--multiline" : ""
      }`}
    >
      <span className="label">{label}</span>
      <div className="input-field-with-label__control-row">
        {wrapped}
        {showSuggestedValue ? (
          <button
            type="button"
            className="input-field-with-label__suggestion"
            aria-label={`Use suggested value ${suggestedValueDisplay}`}
            onClick={onApplySuggestedValue}
          >
            {suggestedValueDisplay}
          </button>
        ) : null}
        {constraintsLabel ? (
          <span className="input-field-with-label__constraints">
            {constraintsLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default InputWithLabel;
